import { Box, Typography, useTheme } from "@mui/material";
import { colors } from "@zfp-akademie-a-s/zfp-design";
import { CHART_PADDING_TOP_COEFICIENT } from "Charts/Utils/CommonChartUtils";
import { generateMortgageRepaymentChartData, getAnnotations } from "Charts/Utils/MortgageRepaymentChartUtils";
import { CurrencyCode } from "Infrastructure/Models/CurrencyCodes";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { formatCurrency } from "Infrastructure/Utils/CurrencyUtils";
import { getYearsInflected } from "Infrastructure/Utils/LanguageUtils";
import { Chart } from "chart.js";
import AnnotationPlugin from "chartjs-plugin-annotation";
import { addMonths, addYears, format } from "date-fns";
import type React from "react";
import { Line } from "react-chartjs-2";

Chart.register(AnnotationPlugin);

type Props = {
  readonly desiredRemainingYears: number;
  readonly inflationRate: number;
  readonly interestRate: number;
  readonly loanInterestRate: number;
  readonly loanPaymentAmount: number;
  readonly monthlyContributionAmount: number;
  readonly remainingPrincipal: number;
  readonly remainingYears: number;
  readonly targetAmount: number;
  readonly referenceStartDate: Date;
};

export const MortgageRepaymentChart: React.FC<Props> = ({
  desiredRemainingYears,
  inflationRate,
  interestRate,
  loanInterestRate,
  loanPaymentAmount,
  monthlyContributionAmount,
  remainingPrincipal,
  remainingYears,
  targetAmount,
  referenceStartDate,
}) => {
  const theme = useTheme();
  const { t } = useResource();

  const data = generateMortgageRepaymentChartData({
    interestRate,
    monthlyContributionAmount,
    loanInterestRate,
    inflationRate,
    loanPaymentAmount,
    remainingPrincipal,
    remainingYears,
  });

  const maxInvestmentValue = data.accumulationPhaseData[data.accumulationPhaseData.length - 1];

  const getDatasets = () => {
    const paddedingTop = maxInvestmentValue * CHART_PADDING_TOP_COEFICIENT;
    return [
      {
        data: [paddedingTop],
        borderColor: "transparent",
        backgroundColor: "transparent",
        order: 0,
      },
      {
        data: data.accumulationPhaseData,
        fill: true,
        pointStyle: "dash",
        borderColor: "transparent",
        backgroundColor: colors.chartPositive,
        order: 1,
        tension: 0,
      },
      {
        data: data.repaymentPhaseData,
        fill: true,
        pointStyle: "dash",
        borderColor: "transparent",
        order: 2,
        backgroundColor: colors.chartNeutral,
        tension: 0,
      },
    ];
  };

  const options = {
    locale: "en-US",
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: (context: any) => {
          let tooltipEl = document.getElementById("chartjs-tooltip");

          if (!tooltipEl) {
            tooltipEl = document.createElement("div");
            tooltipEl.id = "chartjs-tooltip";
            tooltipEl.innerHTML = "<table></table>";
            document.body.appendChild(tooltipEl);
          }

          const tooltipModel = context.tooltip;
          if (tooltipModel.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }

          function getBody(bodyItem: any) {
            return bodyItem.lines;
          }

          let remainingBalance = "";

          if (tooltipModel.body) {
            const bodyLines = tooltipModel.body.map(getBody);
            let innerHtml = "<thead>";

            if (tooltipModel.dataPoints[0].dataIndex <= data.repaymentIndex) {
              innerHtml += `</thead><tbody><tr><td style='font-weight: 500;'>
                    ${t(Resources.AnalysisDetail.Goals.InvestmentDuration)}: ${desiredRemainingYears} ${t(getYearsInflected(desiredRemainingYears))}
                    </td></tr>`;

              remainingBalance = `<span>${t(Resources.AnalysisDetail.Goals.RemainingPrincipal)}: ${formatCurrency(Number(bodyLines[1][0].toString().replaceAll(",", "")), CurrencyCode.CZK, 0)}</span>`;
            }

            const investmentAmount = `<span>${t(Resources.AnalysisDetail.Goals.InvestmentValue)}: ${formatCurrency(Number(bodyLines[0][0].toString().replaceAll(",", "")), CurrencyCode.CZK, 0)}</span>`;
            innerHtml += `<tr><td>${remainingBalance}</td></tr><tr><td>${investmentAmount}</td></tr>`;
            innerHtml += "</tbody>";

            const tableRoot = tooltipEl.querySelector("table");

            if (tableRoot) {
              tableRoot.innerHTML = innerHtml;
            }
          }

          const chartPosition = context.chart.canvas.getBoundingClientRect();
          const tooltipPosition = tooltipEl.getBoundingClientRect();

          const width = tooltipPosition.right - tooltipPosition.left;
          const height = tooltipPosition.bottom - tooltipPosition.top;

          tooltipEl.style.opacity = "1";
          tooltipEl.style.position = "absolute";
          tooltipEl.style.left = `${chartPosition.left + window.pageXOffset + tooltipModel.caretX - width}px`;
          tooltipEl.style.top = `${chartPosition.top + window.pageYOffset + tooltipModel.caretY - height}px`;
          tooltipEl.style.padding = `${tooltipModel.padding}px`;
          tooltipEl.style.pointerEvents = "none";
          tooltipEl.style.zIndex = "10000";

          tooltipEl.style.fontFamily = "Roboto";
          tooltipEl.style.fontSize = "10px";
          tooltipEl.style.lineHeight = "1.1";
          tooltipEl.style.borderWidth = "1px";
          tooltipEl.style.borderStyle = "solid";
          tooltipEl.style.borderColor = colors.borderLight;
          tooltipEl.style.backgroundColor = colors.white;
          tooltipEl.style.borderRadius = theme.spacing(Radius.M);
          tooltipEl.style.padding = theme.spacing(Spacing.M);
        },
      },
      annotation: {
        annotations: {
          ...getAnnotations({
            repaymentIndex: data.repaymentIndex,
            targetAmount,
            remainingPrincipal,
            maxAmount: maxInvestmentValue,
          }),
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          font: {
            color: "black",
            weight: 600,
            size: 10,
            family: "Roboto",
            lineHeight: 1.3,
          },
          callback: (value: string | number, index: number) => {
            const date = referenceStartDate;
            const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);

            // first date
            if (index === 0) {
              return format(addMonths(firstDayOfMonth, Number(value)), "yyyy");
            }

            // repaymentDate
            if (index === data.repaymentIndex) {
              return format(addYears(date, desiredRemainingYears), "yyyy");
            }

            // last date
            if (index === data.accumulationPhaseData.length - 1) {
              return format(addYears(date, remainingYears), "yyyy");
            }

            if (index > data.repaymentPhaseData.length) {
              return "";
            }
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        suggestedMin: 0,
        beginAtZero: true,
        ticks: {
          display: false,
        },
        grid: {
          display: false,
        },
      },
    },
    layout: {
      padding: { right: 32 },
    },
  };

  return (
    <Box>
      <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary">
        {t(Resources.Common.Value)}
      </Typography>
      <Box display="flex" alignItems="end">
        <Line
          options={options}
          data={{
            labels: new Array(data.accumulationPhaseData.length).fill(""),
            datasets: getDatasets(),
          }}
        />
        <Typography variant="caption" fontSize={10} fontWeight={400} lineHeight={1.3} color="secondary" mb={5.2} ml={-5}>
          {t(Resources.Common.Time)}
        </Typography>
      </Box>
    </Box>
  );
};
